import React, { useCallback, useEffect, useState } from 'react';
import stlIcon from '../../../assets/icons/stl.svg';
import xMark from '../../../assets/icons/x-mark.svg';
import Loading from '../../../components/Loading';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import DragAndDropZone from '../../../components/upload/DragAndDropZone';
import PatientSTLCard from '../../../components/upload/PatientSTLCard';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { FileType, useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import { deleteFile, getFiles, uploadFile, type PatientFile } from '../../../services/cloud-files';
import { reportCaughtError } from '../../../services/errorReporting';
import { updatePatientCreation } from '../../../services/patient/creation';

const UploadPatientStls: React.FC = () => {
    const { formData, updateSubmissionForm } = useSubmissionForm();
    const { goNext } = useStepperContext();
    const { setFooterContent } = useLayoutFooter();
    const { showToast } = useToast();

    const [patientSTLs, setPatientSTLs] = useState<FileType[]>([]);
    const [existingSTLs, setExistingSTLs] = useState<PatientFile[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
    const [isModified, setIsModified] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [iteroScanNumber, setIteroScanNumber] = useState<string>('');
    const [isIteroScanNumberModified, setIsIteroScanNumberModified] = useState(false);

    // Upload progress state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    const [totalUploadCount, setTotalUploadCount] = useState(0);
    const [currentFileName, setCurrentFileName] = useState('');
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);

    // Fetch existing STL files from GCS
    const loadExistingFiles = useCallback(async () => {
        if (!formData?.id) return;

        setIsLoadingFiles(true);
        try {
            const files = await getFiles(formData.id, 'STL');
            setExistingSTLs(files);
        } catch (err) {
            // Failed to load existing STL files; list will appear empty
        } finally {
            setIsLoadingFiles(false);
        }
    }, [formData?.id]);

    useEffect(() => {
        if (formData?.id) {
            loadExistingFiles();
        }
    }, [formData?.id, loadExistingFiles]);

    useEffect(() => {
        setIteroScanNumber(formData?.patientIteroScanNumber || '');
    }, [formData?.patientIteroScanNumber]);

    const handleSTLFileUpload = useCallback((files: FileList) => {
        const newSTLs: FileType[] = Array.from(files)
            .filter((file) => file.name.toLowerCase().endsWith('.stl'))
            .map((file, index) => ({
                id: `stl_${Date.now()}_${index}`,
                file,
                preview: stlIcon,
                type: '',
                name: file.name
            }));

        if (newSTLs.length > 0) {
            setPatientSTLs((prev) => [...prev, ...newSTLs]);
            setIsModified(true);
        }
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            if (e.dataTransfer.files) {
                handleSTLFileUpload(e.dataTransfer.files);
            }
        },
        [handleSTLFileUpload]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                handleSTLFileUpload(e.target.files);
            }
        },
        [handleSTLFileUpload]
    );

    const handleRemoveSTL = useCallback((stlId: string) => {
        setPatientSTLs((prev) => prev.filter((stl) => stl.id !== stlId));
        setIsModified(true);
    }, []);

    const handleTypeChange = useCallback((stlId: string, type: string) => {
        setPatientSTLs((prev) =>
            prev.map((stl) => (stl.id === stlId ? { ...stl, type: type.toLowerCase() } : stl))
        );
        setIsModified(true);
    }, []);

    const handleDeleteExisting = useCallback(
        async (patientFileId: string) => {
            if (!formData?.id) return;

            try {
                await deleteFile(formData.id, patientFileId);
                setExistingSTLs((prev) => prev.filter((f) => f.id !== patientFileId));
            } catch (err) {
                // Delete failed; file will remain in list
            }
        },
        [formData?.id]
    );

    const handleNextClick = useCallback(async () => {
        setIsLoading(true);
        if (isModified) {
            updateSubmissionForm({
                patientStls: patientSTLs
            });

            const stlsToUpload = patientSTLs.filter((stl) => stl.file && stl.type);

            if (stlsToUpload.length > 0) {
                setIsUploading(true);
                setUploadErrors([]);
                setTotalUploadCount(stlsToUpload.length);
                const errors: string[] = [];

                for (let i = 0; i < stlsToUpload.length; i++) {
                    const stl = stlsToUpload[i];
                    setCurrentUploadIndex(i + 1);
                    setCurrentFileName(stl.name);
                    setUploadProgress(0);

                    try {
                        await uploadFile(
                            formData?.id as string,
                            stl.file,
                            'STL',
                            `${stl.type} STL`,
                            { stlType: stl.type },
                            (progress) => setUploadProgress(progress)
                        );
                    } catch (error) {
                        const message = error instanceof Error ? error.message : 'Upload failed';
                        errors.push(`Failed to upload ${stl.name}: ${message}`);
                    }
                }

                setIsUploading(false);
                setUploadProgress(0);
                setCurrentUploadIndex(0);
                setTotalUploadCount(0);
                setCurrentFileName('');

                if (errors.length > 0) {
                    setUploadErrors(errors);
                    setIsLoading(false);
                    return;
                }
            }
        }
        if (isIteroScanNumberModified && formData?.id) {
            try {
                await updatePatientCreation(formData.id, {
                    patientIteroScanNumber: iteroScanNumber
                });
                updateSubmissionForm({
                    patientIteroScanNumber: iteroScanNumber
                });
                setIsIteroScanNumberModified(false);
            } catch (error) {
                showToast('Failed to save scan ID. Please try again.', 'error');
                reportCaughtError(error);
                setIsLoading(false);
                return;
            }
        }
        setIsLoading(false);
        goNext();
    }, [
        goNext,
        isModified,
        formData?.id,
        patientSTLs,
        updateSubmissionForm,
        isIteroScanNumberModified,
        iteroScanNumber
    ]);

    useEffect(() => {
        const allTypesAssigned = patientSTLs.every((stl) => stl.type);
        const hasStls = existingSTLs.length > 0 || (patientSTLs.length > 0 && allTypesAssigned);
        const hasIteroNumber = iteroScanNumber.trim() !== '';
        const isButtonDisabled = isLoading || isUploading || !(hasStls || hasIteroNumber);

        setFooterContent(
            <div className="w-full flex justify-end items-center gap-3">
                <MessageOdoFooterButton />
                <button
                    disabled={isButtonDisabled}
                    onClick={handleNextClick}
                    className={`flex flex-row justify-center items-center px-4 py-3.5 gap-2 min-w-32 h-10 rounded-lg font-bold text-sm leading-4 text-center tracking-wider ${
                        isButtonDisabled
                            ? 'bg-gray-300 text-white cursor-not-allowed'
                            : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                >
                    {isUploading ? (
                        <span className="flex items-center gap-1">
                            Uploading
                            <span className="flex gap-0.5">
                                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                                    .
                                </span>
                                <span
                                    className="animate-bounce"
                                    style={{ animationDelay: '150ms' }}
                                >
                                    .
                                </span>
                                <span
                                    className="animate-bounce"
                                    style={{ animationDelay: '300ms' }}
                                >
                                    .
                                </span>
                            </span>
                        </span>
                    ) : (
                        'Continue'
                    )}
                </button>
            </div>
        );

        return () => {
            setFooterContent(null);
        };
    }, [
        setFooterContent,
        isLoading,
        isUploading,
        handleNextClick,
        patientSTLs,
        existingSTLs,
        iteroScanNumber
    ]);

    return (
        <>
            {isLoading && <Loading text="Saving..." />}
            {isLoadingFiles && <Loading text="Loading files..." />}
            <div className="max-w-4xl mx-auto">
                <div className="flex flex-row justify-center items-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-8">Upload STLs</h1>
                </div>
                <p className="text-gray-600 mb-4">
                    <span className="text-gray-900 underline italic font-bold"> Important :</span>{' '}
                    If the patient has been previously treated, then only submit STL files that have
                    been taken after their most recent treatment.
                    {formData?.caseType && formData.caseType.monday_key === 'Pediatrics' && (
                        <span>
                            {' '}
                            If your patient has teeth erupting or you are aware of potential
                            shifting, please upload records no older than 4 weeks for an accurate
                            diagnosis and treatment plan.
                        </span>
                    )}
                </p>

                <div className="mb-8 mt-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter scan ID
                    </label>
                    <input
                        type="text"
                        value={iteroScanNumber}
                        onChange={(e) => {
                            setIteroScanNumber(e.target.value);
                            setIsIteroScanNumberModified(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter scan ID"
                    />
                </div>
                {formData?.manufacturer && formData.manufacturer !== 'Invisalign Aligners' && (
                    <>
                        <div className="flex items-center mt-8 mb-4">
                            <div className="flex-1 border-t-2 border-gray-300"></div>
                            <span className="px-4 text-sm text-gray-500 italic font-bold">or</span>
                            <div className="flex-1 border-t-2 border-gray-300"></div>
                        </div>
                        <div className="flex flex-col pb-6 w-full">
                            <DragAndDropZone
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onFileInputChange={handleFileInputChange}
                                isDragOver={isDragOver}
                                accept=".stl"
                            />
                        </div>
                    </>
                )}
                {/* Existing STL files from GCS */}
                {existingSTLs.length > 0 && (
                    <div className="flex flex-col gap-3 mb-6">
                        <h3 className="text-sm font-bold text-gray-800">Previously Uploaded</h3>
                        <div className="space-y-2">
                            {existingSTLs.map((file) => (
                                <div
                                    key={file.id}
                                    className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <img src={stlIcon} alt="STL" className="w-8 h-8" />
                                        <div className="flex flex-col min-w-0">
                                            <span
                                                className="text-sm text-gray-800 truncate"
                                                title={file.file.originalFilename}
                                            >
                                                {file.file.originalFilename}
                                            </span>
                                            {file.metadata?.stlType && (
                                                <span className="text-xs text-gray-500 capitalize">
                                                    {file.metadata.stlType}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteExisting(file.id)}
                                        className="ml-2 p-2 rounded hover:bg-gray-200 transition-colors"
                                        title="Delete file"
                                    >
                                        <img src={xMark} alt="Remove" className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* New STL files to upload */}
                {patientSTLs.length > 0 && (
                    <div className="flex flex-col gap-3">
                        <h3 className="text-sm font-bold text-gray-800">New Files to Upload</h3>
                        {patientSTLs.some((stl) => !stl.type) && (
                            <p className="text-sm text-red-600 font-medium">
                                Please select a type (Upper jaw, Lower jaw, or Occlusion) for each
                                STL file to continue.
                            </p>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                            {patientSTLs.map((stl) => (
                                <PatientSTLCard
                                    key={stl.id}
                                    stl={stl}
                                    onTypeChange={handleTypeChange}
                                    onRemove={handleRemoveSTL}
                                    showTypeRequired={!stl.type}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Upload Error Messages */}
                {uploadErrors.length > 0 && (
                    <div className="mt-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="text-sm font-bold text-red-800 mb-2">
                                Some files failed to upload
                            </h4>
                            <ul className="list-disc list-inside space-y-1">
                                {uploadErrors.map((err, i) => (
                                    <li key={i} className="text-sm text-red-700">
                                        {err}
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => setUploadErrors([])}
                                className="mt-3 text-sm font-medium text-red-600 hover:underline"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                )}

                {/* Upload Progress Bar */}
                {isUploading && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl p-6 w-96 shadow-xl">
                            <h3 className="text-lg font-bold text-gray-800 mb-4">
                                Uploading STL Files
                            </h3>
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <p
                                        className="text-sm text-gray-600 truncate max-w-[70%]"
                                        title={currentFileName}
                                    >
                                        {currentFileName}
                                    </p>
                                    <p className="text-sm text-gray-500 font-medium">
                                        {currentUploadIndex}/{totalUploadCount}
                                    </p>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                    <div
                                        className="bg-[#3773A8] h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.round(uploadProgress)}%` }}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5 text-right">
                                    {Math.round(uploadProgress)}%
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default UploadPatientStls;
